import { Box, Popover, Stack, TextField, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { HexAlphaColorPicker } from "react-colorful";
import { isValidHexColor } from "../../utils/helpers";

interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  label?: string;
}

const ColorPicker: React.FC<ColorPickerProps> = ({
  value,
  onChange,
  label,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const [inputValue, setInputValue] = useState(value);
  const [error, setError] = useState(false);

  useEffect(() => {
    setInputValue(value);
    setError(false);
  }, [value]);

  const handleButtonClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    if (triggerRef.current) {
      triggerRef.current.focus();
    }
    if (inputValue === value) return;
    if (isValidHexColor(inputValue)) {
      setError(false);
      onChange(inputValue);
    } else {
      setError(true);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setError(false);
  };

  const open = Boolean(anchorEl);
  const idSlug = (label ?? "color-picker")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

  return (
    <Stack spacing={1}>
      {label && <Typography variant="body2">{label}</Typography>}
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          id={`${idSlug}-color-picker-input`}
          size="small"
          value={inputValue}
          placeholder="Enter #RRGGBB or #RRGGBBAA"
          error={error}
          helperText={
            error ? "Enter a valid 6 or 8-digit hex colour" : undefined
          }
          sx={{ flexGrow: 1 }}
          onChange={handleInputChange}
          slotProps={{
            htmlInput: { maxLength: 9 },
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleClose();
            }
          }}
          onBlur={handleClose}
        />
        <Box
          id={`${idSlug}-color-picker-trigger`}
          ref={triggerRef}
          tabIndex={0}
          sx={{
            width: 37,
            height: 37,
            borderRadius: 1,
            backgroundColor: inputValue,
            border: "1px solid #ccc",
          }}
          onClick={handleButtonClick}
        />
      </Stack>

      <Popover
        id={`${idSlug}-color-picker-popover`}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Box p={2}>
          <HexAlphaColorPicker
            color={value}
            onChange={(newColor) => {
              setInputValue(newColor);
              setError(false);
            }}
          />
        </Box>
      </Popover>
    </Stack>
  );
};

export default ColorPicker;
